import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { generalSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditBuildingMutation,
  useOperatorShortQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
} from '@common/cache';
import { paths } from '@common/types';
import { omit } from 'lodash';
import { useContext, useEffect, useState } from 'react';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface IGeneralFormShape {
  name: string;
  organization_id: string;
  partner_id: string;
  operator_id: string;
  google_places_id?: string | null;
  street: string;
  suite?: string | null;
  city: string;
  state: string;
  county?: string | null;
  zip_code: string;
  phone_number?: string | null;
  fax_number?: string | null;
}

const initialValues: Partial<IGeneralFormShape> = {
  name: '',
  organization_id: '',
  operator_id: '',
  partner_id: '',
  google_places_id: '',
  street: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
};

export const useGeneral = (
  building: paths[`/building/{record_id}`]['get']['responses']['200']['content']['application/json'],
) => {
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const { mutateAsync, isPending } = useEditBuildingMutation(building.id);
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();

  const formMethods = useForm<IGeneralFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IGeneralFormShape, any, any>(generalSchema),
    defaultValues: {
      ...initialValues,
      ...omit(building, ['beds']),
      ...building.address,
      organization_id: building.organization.id,
      partner_id: building.partner.id,
      operator_id: building.operator.id,
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = formMethods;

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });
  const part_id = useWatch({
    control: formMethods.control,
    name: 'partner_id',
  });

  const { data: partnerOptions = [], refetch: refetchPartners } =
    usePartnerShortQuery(org_id as string);
  const { data: operatorOptions = [], refetch: refetchOperators } =
    useOperatorShortQuery(part_id);

  useEffect(() => {
    if (org_id) {
      refetchPartners();
    }
  }, [org_id]);

  useEffect(() => {
    if (org_id && part_id) {
      refetchOperators();
    }
  }, [part_id]);

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: IGeneralFormShape) => {
    try {
      await mutateAsync({
        ...values,
        record_id: values.operator_id,
        building_id: building.id,
        country: 'US',
      });

      toast.success('Changes saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    onSubmit();
    reset(getValues());
    setIsFormClean(true);
  };

  return {
    formMethods,
    onSubmit,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSubmitting,
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    operatorOptions: operatorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    setValue,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
